import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Youtube, X } from "lucide-react";
import type {
  YoutubePresets as YoutubePresetsData,
  YoutubePrivacy,
  YoutubeAudience,
} from "@shared/schema";
import { PresetSection, PresetSelect, PresetTextInput } from "./PresetSection";
import { YOUTUBE_CATEGORIES } from "./youtube-categories";

export function YoutubePresets({
  value,
  onChange,
}: {
  value: YoutubePresetsData;
  onChange: (next: YoutubePresetsData) => void;
}) {
  const { t } = useTranslation();
  const [tagDraft, setTagDraft] = useState("");

  function addTag() {
    const cleaned = tagDraft.trim();
    if (!cleaned || value.tags.includes(cleaned)) {
      setTagDraft("");
      return;
    }
    onChange({ ...value, tags: [...value.tags, cleaned] });
    setTagDraft("");
  }

  return (
    <PresetSection
      icon={<Youtube size={14} style={{ color: "#FF0000" }} />}
      title={t("contentScheduler.presets.youtube.title")}
    >
      <div className="grid grid-cols-2 gap-3">
        <PresetTextInput
          label={t("contentScheduler.presets.youtube.videoTitle")}
          value={value.title}
          onChange={(next) => onChange({ ...value, title: next })}
          maxLength={100}
          rightHint={`${value.title.length} / 100`}
        />
        <PresetSelect<YoutubeAudience | "">
          label={t("contentScheduler.presets.youtube.audience")}
          value={value.audience}
          onChange={(next) => onChange({ ...value, audience: next })}
          options={[
            { value: "", label: t("contentScheduler.presets.youtube.audienceUnset") },
            { value: "kids", label: t("contentScheduler.presets.youtube.audienceKids") },
            { value: "not_kids", label: t("contentScheduler.presets.youtube.audienceNotKids") },
          ]}
        />
        <PresetSelect<YoutubePrivacy>
          label={t("contentScheduler.presets.youtube.privacy")}
          value={value.privacy}
          onChange={(next) => onChange({ ...value, privacy: next })}
          options={[
            { value: "public", label: t("contentScheduler.presets.youtube.privacyPublic") },
            { value: "unlisted", label: t("contentScheduler.presets.youtube.privacyUnlisted") },
            { value: "private", label: t("contentScheduler.presets.youtube.privacyPrivate") },
          ]}
        />
        <PresetSelect<string>
          label={t("contentScheduler.presets.youtube.category")}
          value={value.category}
          onChange={(next) => onChange({ ...value, category: next })}
          options={[
            { value: "", label: t("contentScheduler.presets.youtube.categoryUnset") },
            ...YOUTUBE_CATEGORIES.map((c) => ({ value: c.id, label: c.label })),
          ]}
        />
      </div>
      <div className="text-[10px] text-white/35 -mt-1">
        {t("contentScheduler.presets.youtube.privacyHint")}
      </div>
      <PresetTextInput
        label={t("contentScheduler.presets.youtube.playlist")}
        value={value.playlistId}
        onChange={(next) => onChange({ ...value, playlistId: next })}
        placeholder={t("contentScheduler.presets.youtube.playlistPlaceholder")}
      />
      <div>
        <span className="text-[11px] uppercase tracking-wider text-white/45 font-medium">
          {t("contentScheduler.presets.youtube.tags")}
        </span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {value.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] border border-white/[0.1] px-2.5 py-1 text-[11px] text-white/80"
            >
              {tag}
              <button
                type="button"
                onClick={() => onChange({ ...value, tags: value.tags.filter((t) => t !== tag) })}
                className="text-white/40 hover:text-red-400"
              >
                <X size={10} />
              </button>
            </span>
          ))}
          <input
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder={t("contentScheduler.presets.youtube.tagsPlaceholder")}
            className="flex-1 min-w-[140px] bg-white/[0.04] border border-white/[0.08] rounded-full px-3 py-1 text-[11px] text-white/85 placeholder:text-white/30 focus:outline-none focus:border-blue-500/50"
          />
        </div>
      </div>
    </PresetSection>
  );
}
